// Fetches GET /api/v1/workspaces once per session and shares the result between HomePage (the
// card grid) and MapView (the empty-state banner) — both need the same list, and neither should
// each independently refetch it. "Stopping the API shows the error state rather than a blank or
// stale page" (ROADMAP.md v0.6.2's required validation) is what the 'error' status is for.
import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from 'react';
import { fetchWorkspaces, type WorkspaceSummary } from './workspaces';

type WorkspacesState =
  | { status: 'loading' }
  | { status: 'ready'; workspaces: WorkspaceSummary[] }
  | { status: 'error'; message: string };

type WorkspacesContextValue = WorkspacesState & { retry: () => void };

const WorkspacesContext = createContext<WorkspacesContextValue | null>(null);

export function WorkspacesProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<WorkspacesState>({ status: 'loading' });
  const [attempt, setAttempt] = useState(0);

  const load = useCallback(() => {
    let cancelled = false;
    setState({ status: 'loading' });
    fetchWorkspaces()
      .then((workspaces) => {
        if (!cancelled) setState({ status: 'ready', workspaces });
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setState({ status: 'error', message: err instanceof Error ? err.message : 'Unknown error' });
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => load(), [load, attempt]);

  const value: WorkspacesContextValue = { ...state, retry: () => setAttempt((n) => n + 1) };
  return <WorkspacesContext.Provider value={value}>{children}</WorkspacesContext.Provider>;
}

export function useWorkspaces(): WorkspacesContextValue {
  const ctx = useContext(WorkspacesContext);
  if (!ctx) throw new Error('useWorkspaces must be used within WorkspacesProvider');
  return ctx;
}

/** Convenience for callers (e.g. MapView's empty-state banner) that only care about one
 * workspace by slug and don't need to handle loading/error themselves — returns `null` while
 * loading, on error, or if the slug isn't in the visible list. */
export function useWorkspaceBySlug(slug: string | null): WorkspaceSummary | null {
  const state = useWorkspaces();
  if (state.status !== 'ready' || slug === null) return null;
  return state.workspaces.find((w) => w.slug === slug) ?? null;
}
